/* Razorpay Standard Checkout — lazy-loads Checkout.js and drives the pay-verify flow.

Usage:
  <PayWithRazorpay
    orderId={internalOrderId}
    userInfo={{ name, email, contact }}
    onSuccess={(orderId) => ...}
    onCancel={() => ...}
  />
*/
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CreditCard, ShieldCheck } from "@phosphor-icons/react";

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = CHECKOUT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PayWithRazorpay({ orderId, userInfo = {}, onSuccess, onCancel, disabled }) {
  const [enabled, setEnabled] = useState(null);   // null=loading, true/false=known
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/payments/razorpay/status")
      .then((r) => { if (!cancelled) setEnabled(!!r.data.enabled); })
      .catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  const pay = async () => {
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay. Check your internet."); return; }

      const { data } = await api.post("/payments/razorpay/create-order", { order_id: orderId });

      const options = {
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Talaio AI",
        description: `Order ${orderId.slice(0, 8).toUpperCase()}`,
        order_id: data.razorpay_order_id,
        prefill: {
          name: userInfo.name || "",
          email: userInfo.email || "",
          contact: userInfo.contact || "",
        },
        theme: { color: "#DB2777" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: orderId,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful");
            onSuccess?.(orderId);
          } catch (e) {
            toast.error(e.response?.data?.detail || "Payment verification failed");
          }
        },
        modal: {
          ondismiss: () => { onCancel?.(); },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  if (enabled === null) {
    return <button className="pf-btn-primary" disabled data-testid="razorpay-loading">Loading…</button>;
  }
  if (!enabled) {
    return (
      <div className="p-4 border border-amber-400 bg-amber-50 rounded-lg text-sm" data-testid="razorpay-disabled">
        <strong>Payments are not configured yet.</strong>
        <div className="text-amber-900/70 mt-1">
          Admin needs to add <code className="font-mono">RAZORPAY_KEY_ID</code> and <code className="font-mono">RAZORPAY_KEY_SECRET</code> to the backend <code className="font-mono">.env</code>.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="razorpay-block">
      <button
        onClick={pay}
        disabled={busy || disabled}
        className="pf-btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="pay-razorpay"
      >
        {busy ? "Opening Razorpay…" : (<><CreditCard size={16} weight="fill"/> Pay securely with Razorpay</>)}
      </button>
      <div className="flex items-center gap-2 text-xs text-[var(--pf-text-3)] justify-center">
        <ShieldCheck size={14}/> UPI · Cards · Netbanking · Wallets · Secured by Razorpay
      </div>
    </div>
  );
}
